/**
 * @api/userProfile.ts
 * Provides methods through which to make userProfile-specific requests to the API.
 */
import 'whatwg-fetch';

import { ReceiveUserProfiles, RequestUserProfiles } from '@Actions/userProfilesActions';
import { store } from '@ReactStore/store';

import { IUserProfileModel } from '@Shared/models/UserProfile';
import { EditProfileFormValues } from '@Shared/types/api/user_profile';

import { Api as apiService } from '../api';

export interface ISortedProfiles {
  invitedProfiles: IUserProfileModel[];
  activeProfiles: IUserProfileModel[];
  inactiveProfiles: IUserProfileModel[];
}

export abstract class UserProfileApiSvc {
  /**
   * Gets all user profiles for the current user, either from the Redux Store or via the API
   *
   * @param {Boolean<overrideStore>} If truthy, circumvents checking the store and makes an API request
   *
   */
  static getWhitelabeledUserProfiles(overrideStore: boolean = false): Promise<ISortedProfiles> {
    if (!overrideStore) {
      const stateProfiles = store.getState().userProfiles;

      if (stateProfiles.activeProfiles.length > 0 || stateProfiles.inactiveProfiles.length > 0) {
        return Promise.resolve(stateProfiles);
      }
    }

    // request action
    store.dispatch(new RequestUserProfiles());
    return apiService
      .get('/api/web/user_profiles?include_invited=1&include_non_active_companies=1')
      .then((response) => response.json() as any) // <-- typings here are being very annoying. .json should take a type argument and .then should unwrap all promises
      .then((profiles: IUserProfileModel[]) => {
        const sortedProfiles = profiles.sort((a: IUserProfileModel, b: IUserProfileModel) => {
          return a.company.name.toLowerCase() > b.company.name.toLowerCase() ? 1 : -1;
        });

        let invitedProfiles: IUserProfileModel[] = [];
        let activeProfiles: IUserProfileModel[] = [];
        let inactiveProfiles: IUserProfileModel[] = [];

        sortedProfiles.forEach((profile: IUserProfileModel) => {
          if (profile.state === 'invited') {
            return invitedProfiles.push(profile);
          }

          if (profile.company.state === 'active') {
            return activeProfiles.push(profile);
          }

          return inactiveProfiles.push(profile);
        });

        // If whitelabel is enabled, we should filter out only whitelabel config id matching companies.
        if (window.whitelabelConfiguration) {
          const isWhitelabelPredicate = (p: IUserProfileModel) =>
            p.company.whitelabel_configuration_id === window.whitelabelConfiguration.id;

          const whitelabeledActiveProfiles = activeProfiles.filter(isWhitelabelPredicate);
          const whitelabeledInactiveProfiles = inactiveProfiles.filter(isWhitelabelPredicate);
          const whitelabeledInvitedProfiles = invitedProfiles.filter(isWhitelabelPredicate);

          activeProfiles = whitelabeledActiveProfiles.length > 0 ? whitelabeledActiveProfiles : activeProfiles;
          inactiveProfiles = whitelabeledInactiveProfiles.length > 0 ? whitelabeledInactiveProfiles : inactiveProfiles;
          invitedProfiles = whitelabeledInvitedProfiles.length > 0 ? whitelabeledInvitedProfiles : invitedProfiles;
        }

        // success action
        store.dispatch(new ReceiveUserProfiles(invitedProfiles, activeProfiles, inactiveProfiles));

        return {
          invitedProfiles,
          activeProfiles,
          inactiveProfiles
        };
      });
  }

  /**
   * Disables a single userProfile (sets its state to `disabled`)
   *
   * @param {String<userProfileId>} The ID of the userProfile to disable
   *
   */
  static disable(updateData: EditProfileFormValues) {
    const userProfileId = updateData.id;

    if (!userProfileId) {
      throw new Error('Must choose a user account to disable');
    }

    return apiService.responseToJSONAndToastError(apiService.put(`/api/web/user_profiles/${userProfileId}/disable`, updateData));
  }

  static update(userProfileData: EditProfileFormValues) {
    if (!userProfileData.id) {
      throw new Error('Not valid data');
    }

    return apiService
      .put(`/api/web/user_profiles/${userProfileData.id}?include[user]=&include[expense_tags]=`, userProfileData)
      .then((response) => response.json());
  }

  static bulkUpdate(userProfilesData: EditProfileFormValues[]) {
    return apiService
      .put('/api/web/user_profiles?include[]=user&include[]=expense_tags', userProfilesData)
      .then((response) => response.json());
  }

  static resetEmailForwardingToken(userProfileId: string) {
    return apiService
      .post(`/api/web/user_profiles/${userProfileId}/regenerate_receipt_forwarding_email_address`)
      .then((response) => response.json())
      .then(function (userProfile) {
        if (userProfile.id === window.USER_PROFILE.id) {
          window.USER_PROFILE = userProfile;
          window.AbacusDataManagerSeed.user_profile = userProfile;
        }
        return userProfile;
      });
  }

  static resendVerificationEmail() {
    return apiService.post(`/api/web/self/verify/resend`).then((response) => response.json());
  }
}
